
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Logistica() {
  const { t } = useTranslation();

  useEffect(() => {
    // Update page title and meta description
    document.title = `${t('logistics.hero.title')} - Logistics and Ports | Lions Trade Expert`;
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": t('logistics.hero.title'),
      "description": t('logistics.hero.subtitle'),
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logistica`,
      "author": {
        "@type": "Organization",
        "name": "Lions Trade Expert"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Lions Trade Expert",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logistica`
      },
      "about": {
        "@type": "Thing",
        "name": "Brazilian Port Logistics",
        "description": "Information about Brazilian port logistics, bulk carriers, commodity transportation"
      },
      "keywords": "port logistics, bulk carriers, Brazilian ports, commodity transportation, shipping",
      "inLanguage": "en",
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  const shipTypes = [
    {
      name: t('logistics.ships.bulk.name'),
      description: t('logistics.ships.bulk.description'),
      capacity: t('logistics.ships.bulk.capacity'),
      icon: 'ri-ship-line'
    },
    {
      name: t('logistics.ships.containers.name'),
      description: t('logistics.ships.containers.description'),
      capacity: t('logistics.ships.containers.capacity'),
      icon: 'ri-truck-line'
    },
    {
      name: t('logistics.ships.refrigerated.name'),
      description: t('logistics.ships.refrigerated.description'),
      capacity: t('logistics.ships.refrigerated.capacity'),
      icon: 'ri-temp-cold-line'
    }
  ];

  const ports = [
    {
      name: t('logistics.ports.santos.name'),
      description: t('logistics.ports.santos.description'),
      products: t('logistics.ports.santos.products'),
      capacity: t('logistics.ports.santos.capacity'),
      image: 'santos'
    },
    {
      name: t('logistics.ports.paranagua.name'),
      description: t('logistics.ports.paranagua.description'),
      products: t('logistics.ports.paranagua.products'),
      capacity: t('logistics.ports.paranagua.capacity'),
      image: 'paranagua'
    },
    {
      name: t('logistics.ports.vitoria.name'),
      description: t('logistics.ports.vitoria.description'),
      products: t('logistics.ports.vitoria.products'),
      capacity: t('logistics.ports.vitoria.capacity'),
      image: 'vitoria'
    },
    {
      name: t('logistics.ports.itaqui.name'),
      description: t('logistics.ports.itaqui.description'),
      products: t('logistics.ports.itaqui.products'),
      capacity: t('logistics.ports.itaqui.capacity'),
      image: 'itaqui'
    }
  ];

  const storageTypes = [
    {
      name: t('logistics.storage.silos.name'),
      description: t('logistics.storage.silos.description'),
      capacity: t('logistics.storage.silos.capacity'),
      icon: 'ri-building-2-line'
    },
    {
      name: t('logistics.storage.warehouses.name'),
      description: t('logistics.storage.warehouses.description'),
      capacity: t('logistics.storage.warehouses.capacity'),
      icon: 'ri-store-2-line'
    },
    {
      name: t('logistics.storage.refrigerated.name'),
      description: t('logistics.storage.refrigerated.description'),
      capacity: t('logistics.storage.refrigerated.capacity'),
      icon: 'ri-snow-line'
    }
  ];

  const transportModes = [
    {
      name: t('logistics.transport.road.name'),
      percentage: t('logistics.transport.road.percentage'),
      description: t('logistics.transport.road.description'),
      icon: 'ri-truck-line',
      color: 'bg-blue-500'
    },
    {
      name: t('logistics.transport.rail.name'),
      percentage: t('logistics.transport.rail.percentage'),
      description: t('logistics.transport.rail.description'),
      icon: 'ri-train-line',
      color: 'bg-green-500'
    },
    {
      name: t('logistics.transport.waterway.name'),
      percentage: t('logistics.transport.waterway.percentage'),
      description: t('logistics.transport.waterway.description'),
      icon: 'ri-ship-2-line',
      color: 'bg-cyan-500'
    }
  ];

  const challenges = [
    {
      title: t('logistics.challenges.infrastructure.title'),
      description: t('logistics.challenges.infrastructure.description'),
      icon: 'ri-anchor-line'
    },
    {
      title: t('logistics.challenges.costs.title'),
      description: t('logistics.challenges.costs.description'),
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: t('logistics.challenges.sustainability.title'),
      description: t('logistics.challenges.sustainability.description'),
      icon: 'ri-leaf-line'
    },
    {
      title: t('logistics.challenges.digitalization.title'),
      description: t('logistics.challenges.digitalization.description'),
      icon: 'ri-computer-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/b1f01a5e0744828409e03367e6e393b1.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
              {t('logistics.hero.title')}
            </h1>
            <h2 className="text-4xl md:text-5xl font-light mb-8 text-cyan-300">
              {t('logistics.hero.subtitle')}
            </h2>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* Ships Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-cyan-800 bg-clip-text text-transparent">
              {t('logistics.ships.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {shipTypes.map((ship, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${ship.icon} text-cyan-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{ship.name}</h3>
                <p className="text-gray-600 mb-4">{ship.description}</p>
                <div className="text-sm text-gray-500">
                  <strong>Capacidade:</strong> {ship.capacity}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ports Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-cyan-800 bg-clip-text text-transparent">
              {t('logistics.ports.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ports.map((port, index) => (
              <div key={index} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-bold mb-3 text-cyan-800">{port.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{port.description}</p>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong className="text-gray-700">Produtos:</strong>
                    <p className="text-gray-600">{port.products}</p>
                  </div>
                  <div>
                    <strong className="text-gray-700">Capacidade:</strong>
                    <p className="text-gray-600">{port.capacity}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storage Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-cyan-800 bg-clip-text text-transparent">
              {t('logistics.storage.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {storageTypes.map((storage, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-md text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${storage.icon} text-cyan-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4">{storage.name}</h3>
                <p className="text-gray-600 mb-4">{storage.description}</p>
                <div className="text-sm text-gray-500">
                  <strong>Capacidade típica:</strong> {storage.capacity}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transport Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-900 via-cyan-800 to-cyan-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
              {t('logistics.transport.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">{t('logistics.transport.modes')}</h3>
              <div className="space-y-6">
                {transportModes.map((mode, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className={`w-12 h-12 ${mode.color} rounded-lg flex items-center justify-center`}>
                      <i className={`${mode.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-bold">{mode.name}</h4>
                        <span className="font-bold text-gray-600">{mode.percentage}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{mode.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">{t('logistics.transport.characteristics.title')}</h3>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold mb-2">{t('logistics.transport.characteristics.distances.title')}</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('logistics.transport.characteristics.distances.farm')}</li>
                    <li>• {t('logistics.transport.characteristics.distances.warehouse')}</li>
                    <li>• {t('logistics.transport.characteristics.distances.time')}</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold mb-2">{t('logistics.transport.characteristics.costs.title')}</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('logistics.transport.characteristics.costs.percent')}</li>
                    <li>• {t('logistics.transport.characteristics.costs.fuel')}</li>
                    <li>• {t('logistics.transport.characteristics.costs.tolls')}</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold mb-2">{t('logistics.transport.characteristics.seasonality.title')}</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('logistics.transport.characteristics.seasonality.peak')}</li>
                    <li>• {t('logistics.transport.characteristics.seasonality.demand')}</li>
                    <li>• {t('logistics.transport.characteristics.seasonality.increase')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-cyan-800 bg-clip-text text-transparent">
              {t('logistics.challenges.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {challenges.map((challenge, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${challenge.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold mb-3">{challenge.title}</h3>
                <p className="text-gray-300 text-sm">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-cyan-800 bg-clip-text text-transparent">
              {t('logistics.numbers.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">180M</div>
              <div className="text-cyan-100">{t('logistics.numbers.tonnage')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">36</div>
              <div className="text-cyan-100">{t('logistics.numbers.ports')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">1.200</div>
              <div className="text-cyan-100">{t('logistics.numbers.terminals')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">15%</div>
              <div className="text-cyan-100">{t('logistics.numbers.gdp')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div 
              className="h-80 rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Large%20storage%20silos%20and%20warehouses%20for%20sugar%20and%20coffee%20at%20Brazilian%20export%20facility%2C%20modern%20industrial%20storage%20infrastructure%2C%20bulk%20commodity%20storage%2C%20professional%20industrial%20photography%2C%20bright%20daylight%2C%20high%20detail%2C%20clean%20composition&width=600&height=400&seq=storage-silos&orientation=landscape')`,
                backgroundSize: 'cover',
                backgroundPosition: 'object-top'
              }}
            ></div>
            <div 
              className="h-80 rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Heavy%20trucks%20transporting%20commodities%20on%20Brazilian%20highways%2C%20modern%20cargo%20transportation%2C%20logistics%20convoy%2C%20professional%20transportation%20photography%2C%20bright%20daylight%2C%20road%20infrastructure%2C%20high%20detail%2C%20clean%20composition&width=600&height=400&seq=truck-transport&orientation=landscape')`,
                backgroundSize: 'cover',
                backgroundPosition: 'object-top'
              }}
            ></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
